import { FEATURES } from '@constants/Text'

// Engine state machine — 7 statuses (6 from React handoff + 'versao_fechada'
// introduzido pelo fluxo de "Fechar versão" da Controladoria V2). Quando o
// usuário publica a versão (POST /fechamentos/fechar-versao), os motores que
// estavam em SUCESSO migram para VERSAO_FECHADA (cadeado).
export const ENGINE_STATUS = {
  NUNCA: 'nunca',
  PENDENTE: 'pendente',
  RODANDO: 'rodando',
  AVISOS: 'avisos',
  SUCESSO: 'sucesso',
  ERRO: 'erro',
  VERSAO_FECHADA: 'versao_fechada',
}

// Color/icon/label visuals for each engine status — sourced from StatusColors.
export { ENGINE_STATUS_VISUALS as ENGINE_STATUS_MAP } from '@modules/controladoria-v2/constants/StatusColors'

// Compact dot color for the MonthHeader engine pills.
export { ENGINE_PILL_DOT_COLORS as ENGINE_PILL_COLORS } from '@modules/controladoria-v2/constants/StatusColors'

// Status of each DRE in the FechamentosList (one cell per engine).
export { FECHAMENTO_DRE_STATUS_VISUALS as FECHAMENTO_DRE_STATUS_MAP } from '@modules/controladoria-v2/constants/StatusColors'

// Backend status_code -> visual key in FECHAMENTO_DRE_STATUS_MAP.
// null=Nunca Executado, 100=Em execução, 200=Sucesso, 300=Versão Fechada,
// 400/500/502=Erro/Timeout, 450=Pendente atualização (rodou mas os dados de
// origem mudaram desde então — espelha ENGINE_STATUS.PENDENTE nas pills do motor).
export const STATUS_CODE_TO_VISUAL_KEY = {
  null: 'nunca',
  100: 'processando',
  200: 'disponibilizado',
  300: 'versao_fechada',
  400: 'erro',
  450: 'pendente',
  500: 'erro',
  502: 'erro',
}

// Backend status_code -> ENGINE_STATUS visual key (used by EngineStatusPill in
// the MonthHeader). Distinct from STATUS_CODE_TO_VISUAL_KEY because the engine
// pill uses a richer status set: nunca/pendente/rodando/avisos/sucesso/erro.
//
// Mapping rules (sucesso may be upgraded to avisos by engineVisualKeyFromRecord
// when the record carries non-empty `warnings`):
//   null  -> nunca           (never executed)
//   100   -> rodando         (executing)
//   200   -> sucesso         (success — possibly upgraded to avisos)
//   300   -> versao_fechada  (version closed — engine result is locked)
//   400   -> erro            (config/data error)
//   450   -> pendente        (last run finished but source data changed since)
//   500   -> erro            (unexpected error)
//   502   -> erro            (timeout)
export const ENGINE_STATUS_CODE_TO_VISUAL_KEY = {
  null: ENGINE_STATUS.NUNCA,
  100: ENGINE_STATUS.RODANDO,
  200: ENGINE_STATUS.SUCESSO,
  300: ENGINE_STATUS.VERSAO_FECHADA,
  400: ENGINE_STATUS.ERRO,
  450: ENGINE_STATUS.PENDENTE,
  500: ENGINE_STATUS.ERRO,
  502: ENGINE_STATUS.ERRO,
}

// Resolves the visual key for a single engine record returned by the backend.
// `record` shape (from get_engine_status_for_month / list_fechamentos):
//   { status_code, status, warnings, errors, em_execucao, ... }
// `null` / `undefined` records fall back to NUNCA so consumers can render a
// neutral pill without extra guard clauses.
export const engineVisualKeyFromRecord = (record) => {
  if (!record) return ENGINE_STATUS.NUNCA
  const code =
    record.status_code === undefined ? null : record.status_code
  const base = ENGINE_STATUS_CODE_TO_VISUAL_KEY[code] || ENGINE_STATUS.NUNCA
  if (base !== ENGINE_STATUS.SUCESSO) return base
  const warnings = record.warnings
  if (Array.isArray(warnings)) {
    return warnings.length > 0 ? ENGINE_STATUS.AVISOS : ENGINE_STATUS.SUCESSO
  }
  if (warnings && typeof warnings === 'object') {
    return Object.keys(warnings).length > 0
      ? ENGINE_STATUS.AVISOS
      : ENGINE_STATUS.SUCESSO
  }
  return ENGINE_STATUS.SUCESSO
}

// Month-grid status (used by Phase 7 lote modais).
export const FECHAMENTO_MES_STATUS = {
  ABERTO: 'aberto',
  PUBLICADO: 'publicado',
  NAO_INICIADO: 'nao_iniciado',
}

// Engines.
export const ENGINE_IDS = {
  PJ: 'pj',
  FP: 'fp',
  PRODUTO: 'produto',
  DFC: 'dfc',
}

export const ENGINE_LABELS = {
  pj: 'DRE Geral',
  fp: 'DRE Profissional, Filial e Equipe',
  produto: 'DRE Área de Produto',
  dfc: 'DFC',
}

export const ENGINE_LIST = [
  { id: 'pj', title: 'DRE Geral' },
  { id: 'fp', title: 'DRE Profissional, Filial e Equipe' },
  { id: 'produto', title: 'DRE Área de Produto' },
  { id: 'dfc', title: 'DFC' },
]

// Short labels rendered inside the MonthHeader status pills.
// Shared with the status-motor-mensagem modal so the title matches the pill the
// user clicked.
export const ENGINE_PILL_LABELS = {
  pj: 'DRE Geral',
  fp: 'Profissional, Filial e Equipe',
  produto: 'Área de Produto',
  dfc: 'DFC',
}

// Outcome distribution for runEngine simulation (mirrors React: ~70% sucesso, ~30% avisos).
export const ENGINE_OUTCOME_WEIGHTS = {
  sucesso: 0.7,
  avisos: 0.3,
  erro: 0,
}

// Simulated work timings (ms). Centralised so tests can stub them.
export const ENGINE_RUN_DELAY_MS_MIN = 1000
export const ENGINE_RUN_DELAY_MS_MAX = 4000
export const ENGINE_STAGGER_MS = 350
export const REPLICAR_TICK_MS = 280
export const RODAR_LOTE_TICK_MS = 180
export const IMPORTAR_LOTE_TICK_MS = 140
export const IMPORT_FILE_DELAY_MS = 600

// Minimum perceived loading duration (ms) — callers `await api()` then
// `await wait(MIN_PERCEIVED_LOADING_MS)` so spinners always render long enough
// for users to perceive a smooth transition.
export const MIN_PERCEIVED_LOADING_MS = 600

// Outcomes for engine state machine (sucesso/avisos only — erro requires explicit failure).
export const ENGINE_OUTCOMES = ['sucesso', 'avisos']

// Mapeia `engine.id` -> chave em FEATURES exigida para o motor ser visível.
// Motores sem entrada aqui (pj, dfc) são sempre visíveis.
export const ENGINE_FEATURE_FLAGS = {
  [ENGINE_IDS.FP]: FEATURES.P_L_PROFISSIONAL,
  [ENGINE_IDS.PRODUTO]: FEATURES.P_L_AREA_DE_RECEITA,
}

// Filtra ENGINE_LIST pelas feature flags do escritório (`$access`).
// Recebe o objeto retornado por `store.getters['parameters/show']`.
export const filterEnginesByAccess = (access) =>
  ENGINE_LIST.filter((engine) => {
    const flag = ENGINE_FEATURE_FLAGS[engine.id]
    return !flag || !!(access && access[flag])
  })

// Verifica se um motor específico está visível para o `$access` informado.
export const isEngineAccessible = (engineId, access) => {
  const flag = ENGINE_FEATURE_FLAGS[engineId]
  return !flag || !!(access && access[flag])
}
